import Button from 'ext-ui/components/Button/Button'
import React, { ReactElement, useEffect, useRef, useState } from 'react'
import { usePrevious } from 'src/hooks'
import { useCurrencyFormatter } from 'src/localization/Crncy'
import { useTranslate } from 'src/localization/Trns'
import { CreditCardListModel } from 'src/models/creditCard.models'
import { toDollars } from 'src/utils/currency.utils'

import { settingsAnchors } from '../../settingsAnchors'
import SettingsDrawerShell from '../SettingsDrawerShell/SettingsDrawerShell'
import SettingsTile from '../SettingsTile/SettingsTile'
import LimitAdjustSection from './LimitAdjustSection'
import LimitStateNote, { limitIsAdjustable } from './LimitStateNote'
import LimitSummary from './LimitSummary'
import style from './SpendLimitTile.style'

/** The subset of the limits snapshot this tile renders and fences on. */
export type SpendLimitSnapshot = {
  /** Minor units. */
  permanentLimit: number
  currency: string
  /** Optimistic-concurrency fence for the NEXT limit change. */
  version: number
}

export type SpendLimitState = 'loading' | 'ready' | 'unavailable' | 'error'

/**
 * The company line this card draws on. `availableCredit` is SIGNED — zero means
 * no headroom and negative means the line is reported overdrawn. Never clamp
 * it; the tile's copy already tells the user increases apply "within your
 * company line", so hiding a negative would contradict that.
 */
export type CompanyLineSummary = {
  name: string
  totalCredit: number
  availableCredit: number
  /**
   * The company line's OWN currency. Not the card's: the "fails closed on
   * currency disagreement" guarantee belongs to the limits contract, and
   * nothing promises this resource shares the card's currency. Formatting it
   * with the card's would silently mislabel the amount.
   */
  currency: string
}

type SpendLimitTileProps = {
  /** Grid-column class from the bento parent. */
  className?: string
  creditCard: CreditCardListModel
  /**
   * Admin/Owner.
   *
   * On the CONNECTED path this only chooses the wording ("Update limit" vs
   * "Apply") and whether the read-only company-line row and its figure appear —
   * it does NOT gate whether a change is routed for approval, because the
   * limits contract has no approval routing to route to. Only the server's
   * `APPLY_PHYSICAL_CARD_LIMIT_CHANGE` capability decides who may mutate.
   *
   * It still drives the request-vs-apply split on the unconnected preview path,
   * which mocks the eventual governed flow.
   */
  canApplyDirectly: boolean
  /**
   * EX-40631 follow-on. `true` reads and writes the Card Management limits
   * resource instead of rendering the preview form. The page owns the gate,
   * the read, and the write.
   */
  useLimitsApi?: boolean
  limits?: SpendLimitSnapshot
  limitsState?: SpendLimitState
  /**
   * Applies a PERMANENT limit change. Temporary limits have no contract — the
   * server rejects any other `kind` with CARD-MGMT.8 — so the tile never offers
   * one on the connected path.
   */
  onApplyPermanentLimit?: (permanentLimitMinorUnits: number) => void
  isApplyingLimit?: boolean
  /** Server-authored message; distinct recoveries must not collapse to one. */
  limitSaveError?: string
  limitSaveSucceeded?: boolean
  /**
   * Fired once a limit save lands. The drawer closes on success, so the page
   * confirms — same contract as the billing drawer's `onSaved`.
   */
  onLimitSaved?: () => void
  /**
   * `APPLY_PHYSICAL_CARD_LIMIT_CHANGE`. Gated independently of the read — a
   * user may legitimately see the limit and be unable to change it, so the
   * read verdict must not be treated as permission to mutate.
   */
  canApplyLimitChange?: boolean
  /** Read-only company-line summary. Composed here, not aggregated server-side. */
  companyLine?: CompanyLineSummary
}

export default function SpendLimitTile(
  props: SpendLimitTileProps,
): ReactElement {
  const {
    className,
    creditCard,
    canApplyDirectly,
    useLimitsApi = false,
    limits,
    limitsState = 'loading',
    onApplyPermanentLimit,
    isApplyingLimit = false,
    limitSaveError,
    limitSaveSucceeded = false,
    onLimitSaved,
    canApplyLimitChange = false,
    companyLine,
  } = props
  const translate = useTranslate()
  const formatCurrency = useCurrencyFormatter()
  const [drawerOpen, setDrawerOpen] = useState(false)
  /*
   * The drawer's action rail, once it exists. State rather than a ref so the
   * portal re-renders when the node arrives — a ref would still be null on the
   * render that mounts it, and the action would never appear.
   */
  const [actionsSlot, setActionsSlot] = useState<HTMLDivElement | null>(null)
  const adjustTriggerRef = useRef<HTMLButtonElement>(null)

  /*
   * Held in a ref rather than an effect dependency. Callers are not required to
   * memoize it, and an unstable callback in the settle effect's deps re-fires
   * it on every render — the failure mode `src/CLAUDE.md` documents. The
   * effect's own isApplying-transition guard is what makes it fire once.
   */
  const onLimitSavedRef = useRef(onLimitSaved)
  useEffect(() => {
    onLimitSavedRef.current = onLimitSaved
  }, [onLimitSaved])

  /*
   * Close the drawer once a connected save lands, and hand confirmation to the
   * page — the same settle contract the billing drawer already uses.
   *
   * The drawer unmounts on close, so the form's own "Saved" note is not a
   * confirmation the user can read; the page raises a toast instead. A FAILED
   * save keeps the drawer open, where `limitSaveError` renders beside the
   * action and the amount can be corrected and resubmitted.
   *
   * Keyed on the isApplying -> settled TRANSITION, not on `limitSaveSucceeded`
   * alone: that flag is the mutation's own `isSuccess` and stays true for its
   * whole life, so depending on it directly would slam the drawer shut every
   * time the user reopened it after a successful change.
   *
   * The drawer can be dismissed while a save is still in flight, so the two
   * halves are gated differently: the confirmation always fires, because the
   * save genuinely succeeded and the toast is the only signal the user gets,
   * while closing and returning focus to the trigger only apply to a drawer
   * that is still open.
   */
  const previouslyApplying = usePrevious(isApplyingLimit)
  useEffect(() => {
    if (previouslyApplying === true && !isApplyingLimit && limitSaveSucceeded) {
      onLimitSavedRef.current?.()
      if (drawerOpen) {
        setDrawerOpen(false)
        adjustTriggerRef.current?.focus()
      }
    }
  }, [drawerOpen, previouslyApplying, isApplyingLimit, limitSaveSucceeded])

  // On the connected path the authoritative Card Management snapshot is the
  // source; the card's own issuing limit is a different number from a different
  // system and must not be shown beside a fence taken from the snapshot.
  const limitCents = useLimitsApi
    ? limits?.permanentLimit
    : (creditCard.issuingLimitCents ?? creditCard.issuerAccountCreditLimitCents)
  // The card's currency is the display source even on the connected path. The
  // limits read fails closed when its persisted component currencies disagree
  // with the card, so the snapshot's currency can never legitimately differ —
  // and it arrives as a bare wire string rather than the typed ExtendCurrency
  // the formatter takes.
  const limitCurrency = creditCard.currency
  // The company line carries its own currency and is a separate resource with
  // its own failure mode. `currencyFormatter` validates the code at runtime and
  // falls back, so the wire string is safe to hand it directly.
  const companyLineCurrency = (companyLine?.currency ??
    creditCard.currency) as typeof creditCard.currency
  // No invented fallback: when the card carries no limit we say so rather than
  // rendering a plausible-looking number the user could act on.
  const formattedLimit =
    limitCents !== undefined
      ? formatCurrency({
          amount: toDollars(limitCents),
          currency: limitCurrency,
          maximumFractionDigits: 0,
        })
      : undefined

  /**
   * "Not available" is a CLAIM about this card — the server said the limit is
   * denied or the card is not enrolled. It is not what an unresolved read looks
   * like, and saying it while capabilities and `/limits` are still in flight
   * made the row flash "Not available" and then swap to the real figure.
   *
   * The unconnected path has no round trip: `limitCents` comes off the credit
   * card synchronously, so an absent value there really is an absent limit.
   */
  const limitValueText = ((): string => {
    if (!useLimitsApi) return formattedLimit ?? translate('Not available')
    if (limitsState === 'loading') return translate('Loading')
    // A failed read is not an absent limit either — the body carries the
    // retry copy, so this must not assert a value we never received.
    if (limitsState === 'error') return translate('Unavailable')
    return formattedLimit ?? translate('Not available')
  })()

  const canAdjustLimit = limitIsAdjustable(
    useLimitsApi,
    limitsState,
    canApplyLimitChange,
  )

  return (
    <SettingsTile
      className={className}
      id={settingsAnchors.spendLimit}
      title={translate('Spend limit')}
    >
      <div className="SpendLimitTile" css={style}>
        <LimitSummary
          limitValueText={limitValueText}
          showAdjustablePill={
            formattedLimit !== undefined &&
            (!useLimitsApi || canApplyLimitChange)
          }
          companyLine={companyLine}
          companyLineCurrency={companyLineCurrency}
          canApplyDirectly={canApplyDirectly}
        />
        {/*
          Why adjusting is impossible, when it is — inline on the tile rather
          than behind the button, so the state is readable without a click.
        */}
        <LimitStateNote
          useLimitsApi={useLimitsApi}
          limitsState={limitsState}
          canApplyLimitChange={canApplyLimitChange}
        />
        {/*
          No button in the states LimitStateNote just explained: it would
          promise an action the drawer immediately walks back.
        */}
        {canAdjustLimit && (
          <Button
            className="SpendLimitTile-adjustTrigger"
            ref={adjustTriggerRef}
            variant="secondary"
            dropShadow={false}
            onClick={() => setDrawerOpen(true)}
            data-testid="adjust-spend-limit"
          >
            {translate('Adjust spend limit')}
          </Button>
        )}
      </div>

      <SettingsDrawerShell
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={translate('Adjust spend limit')}
        lead={translate(
          'Set a permanent or temporary limit. Changes are recorded in Activity.',
        )}
        /* The rail is the destination for the form's own action, which
           `LimitAdjustSection` portals into it — see `actionsSlot`. It carries
           the tile's styles because it renders outside the content card, where
           the action's note classes would otherwise fall out of scope, and it
           stays empty in the pending/applied state (nothing to submit). */
        action={<div css={style} ref={setActionsSlot} />}
      >
        <div className="SpendLimitTile-drawerContent" css={style}>
          <LimitAdjustSection
            actionsSlot={actionsSlot}
            showHeading={false}
            currency={limitCurrency}
            canApplyDirectly={canApplyDirectly}
            useLimitsApi={useLimitsApi}
            limitCents={limitCents}
            onApplyPermanentLimit={onApplyPermanentLimit}
            isApplyingLimit={isApplyingLimit}
            limitSaveError={limitSaveError}
            limitSaveSucceeded={limitSaveSucceeded}
            companyLine={companyLine}
            companyLineCurrency={companyLineCurrency}
          />
        </div>
      </SettingsDrawerShell>
    </SettingsTile>
  )
}
